import { computed, toValue } from 'vue'
import type { MaybeRefOrGetter } from 'vue'
import { useQuery } from '@tanstack/vue-query'

import { getBalance, getBalanceChart, getBalanceSummary } from '../api'
import type { BalanceRangeParams } from '../api'

import { balanceKeys } from './keys'

export function useBalance() {
  const query = useQuery({
    queryKey: balanceKeys.all,
    queryFn: ({ signal }) => getBalance({ signal })
  })

  return {
    ...query,
    balance: computed(() => query.data.value?.data.data.balance ?? null),
    availableToWithdraw: computed(
      () => query.data.value?.data.data.availableToWithdraw ?? null
    ),
    inProcessing: computed(
      () => query.data.value?.data.data.inProcessing ?? null
    ),
    reservedForFees: computed(
      () => query.data.value?.data.data.reservedForFees ?? null
    ),
    usageFee: computed(() => query.data.value?.data.data.usageFee ?? null),
    nextUsageFeeOn: computed(
      () => query.data.value?.data.data.nextUsageFeeOn ?? null
    ),
    currency: computed(() => query.data.value?.data.data.currency ?? ''),
    productName: computed(() => query.data.value?.data.data.productName ?? '')
  }
}

export function useBalanceChart(params: MaybeRefOrGetter<BalanceRangeParams>) {
  const paramsValue = computed(() => toValue(params))

  const query = useQuery({
    queryKey: computed(() => [...balanceKeys.all, 'chart', paramsValue.value]),
    queryFn: ({ signal }) => getBalanceChart(paramsValue.value, { signal })
  })

  return {
    ...query,
    points: computed(() => query.data.value?.data.data ?? [])
  }
}

export function useBalanceSummary(
  params: MaybeRefOrGetter<BalanceRangeParams>
) {
  const paramsValue = computed(() => toValue(params))

  const query = useQuery({
    queryKey: computed(() => [
      ...balanceKeys.all,
      'summary',
      paramsValue.value
    ]),
    queryFn: ({ signal }) => getBalanceSummary(paramsValue.value, { signal })
  })

  return {
    ...query,
    summary: computed(() => query.data.value?.data.data ?? null)
  }
}
